import { FeaturedUpsell, FeaturedUpsellMode } from 'src/dao/core-dao'
import { logger } from 'src/util/logger'

const STORAGE_KEY = 'grndhouse.featured-upsell.state.v1'

type UpsellState = {
  /** How many times the upsell has been shown to this viewer. */
  shownCount: number
  /** Epoch ms of the last time it was shown. */
  lastShownAt?: number
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Every read and write is wrapped: a private window, a browser with site data blocked, or a
 * full quota all throw on access. The upsell is decoration on a completion modal — losing its
 * frequency memory must never stop that modal rendering.
 */
const readState = (): UpsellState => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { shownCount: 0 }
    const parsed = JSON.parse(raw) as UpsellState
    return { shownCount: parsed.shownCount ?? 0, lastShownAt: parsed.lastShownAt }
  } catch (err) {
    logger.debug('featured upsell state unreadable; treating as first view', err)
    return { shownCount: 0 }
  }
}

const writeState = (state: UpsellState): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (err) {
    logger.debug('featured upsell state not persisted', err)
  }
}

/**
 * Decides whether the post-workout upsell should appear, at the cadence the server publishes
 * on `GET /hustlers/me/config`.
 *
 * ⚠️ The counting is **per browser**, not per account: the backend holds no per-user upsell
 * state by design, so this is `localStorage` and a reinstall, a second device or a private
 * window all start again from zero. That is a known and accepted limit of the v1 cadence, and
 * it does not apply to `EVERY` — the default — which needs no state at all.
 */
const shouldShow = (config: FeaturedUpsell, now: number = Date.now()): boolean => {
  if (config.mode === FeaturedUpsellMode.OFF) return false
  if (config.mode === FeaturedUpsellMode.EVERY) return true

  const { shownCount, lastShownAt } = readState()
  if (shownCount < config.firstN) return true
  if (!lastShownAt) return true
  return now - lastShownAt >= config.cooldownDays * DAY_MS
}

export const featuredUpsellService = {
  shouldShow,

  /**
   * The full post-completion gate: whether the paywall should come up on its own once the
   * "Great Work!" modal has closed.
   *
   * Kept here rather than inline in `workout-video.tsx` so the decision is testable without
   * mounting the whole player, and so all three conditions read in one place:
   *
   * - the account cannot already play the whole catalog — otherwise there is nothing to sell;
   * - the workout just finished was a *free* one, so the pitch lands on someone who has just
   *   experienced the product rather than on a paying member's own content;
   * - the server-published cadence allows it.
   *
   * Pure: the caller advances the cadence with `recordShown()` only if it acts on `true`.
   */
  shouldOpenAfterCompletion: ({
    hasFullCatalogAccess,
    isFreeWorkout,
    config,
    now,
  }: {
    hasFullCatalogAccess: boolean
    isFreeWorkout: boolean
    config: FeaturedUpsell
    now?: number
  }): boolean => {
    if (hasFullCatalogAccess) return false
    if (!isFreeWorkout) return false
    return shouldShow(config, now)
  },

  /** Call once when the upsell is actually rendered, so the cadence advances. */
  recordShown: (now: number = Date.now()): void => {
    const { shownCount } = readState()
    writeState({ shownCount: shownCount + 1, lastShownAt: now })
  },

  /** Test seam. */
  __reset: (): void => {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch (_err) {
      // nothing to reset
    }
  },
}
